import { useState } from "react";
import { testBotReply } from "../../../api/login.service";

type Bubble = {
  id: string;
  mine: boolean;
  text: string;
};

type Props = {
  businessName: string;
  phoneNumberId: string;
  businessNumber: string;
  wabaId: string;
};

export default function BotTrainerPhone({
  businessName,
  phoneNumberId,
  businessNumber,
  wabaId,
}: Props) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    setSending(true);
    setBubbles((current) => current.concat({ id: `me-${Date.now()}`, mine: true, text }));
    try {
      const result = await testBotReply({
        channel: "whatsapp",
        text,
        phoneNumberId,
        businessNumber,
        wabaId,
      });
      const reply = result.reply?.trim() || result.error || "No reply.";
      setBubbles((current) => current.concat({ id: `bot-${Date.now()}`, mine: false, text: reply }));
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not test the bot.";
      setBubbles((current) => current.concat({ id: `bot-${Date.now()}`, mine: false, text: message }));
    } finally {
      setSending(false);
    }
  };

  return (
    <aside className="w-[320px] shrink-0 lg:sticky lg:top-4">
      <div className="mx-auto w-[300px] rounded-[36px] border-[10px] border-gray-900 bg-gray-900 shadow-xl">
        <div className="overflow-hidden rounded-[26px] bg-white">
          <div className="flex justify-center bg-gray-900 py-2">
            <span className="h-4 w-24 rounded-full bg-black" />
          </div>
          <div className="bg-[#075e54] px-3 py-3 text-white">
            <p className="text-sm font-semibold">{businessName.trim() || "Your business"}</p>
            <p className="text-[11px] text-white/80">Practice</p>
          </div>
          <div className="flex h-80 flex-col gap-2 overflow-y-auto bg-[#ece5dd] p-3">
            {bubbles.length === 0 ? (
              <p className="m-auto px-3 text-center text-xs text-gray-500">
                Type as a customer. The reply uses the system prompt, brand voice, and business rules saved for this business, plus the closest passages from the uploaded knowledge.
              </p>
            ) : null}
            {bubbles.map((bubble) => (
              <div key={bubble.id} className={`flex ${bubble.mine ? "justify-end" : "justify-start"}`}>
                <p className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${
                  bubble.mine ? "bg-[#dcf8c6]" : "bg-white text-gray-800"
                }`}>
                  {bubble.text}
                </p>
              </div>
            ))}
          </div>
          <div className="flex gap-2 border-t bg-white p-2">
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") send();
              }}
              placeholder="Message"
              className="min-w-0 flex-1 rounded-full border border-gray-200 px-3 py-2 text-xs"
            />
            <button type="button" onClick={send} disabled={sending} className="rounded-full bg-gray-900 px-3 text-xs font-medium text-white disabled:opacity-50">
              {sending ? "..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
